import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { FileProgress, UploadBatch, UploadError } from "../types";
import { apiFetch } from "../utilities/api";
import { uploadBatchService } from "./uploadBatchService";
import { allowedFileTypesToUpload } from "../constants/upload";
import { UPLOAD_OPTIONS } from "../constants/common";
import { createS3Uppy, describeUploadError, EngineFile } from "./upload/createS3Uppy";
import { mediaAdapter } from "./upload/adapters";
import { applyAssetStatus } from "./assetStatusCache";

/**
 * Media uploads (the Upload page, entity forms, form settings), on the shared S3
 * upload engine (`services/upload`).
 *
 * Each file gets a media row and goes straight to S3: up to 5 MB in one signed
 * PUT, larger in 10 MB parts that resume after a failure or an interrupted
 * page. Once a file has landed its row is marked completed, which also counts
 * it in its upload batch.
 *
 * `uploadFiles` resolves with what succeeded and what failed rather than
 * throwing, so the files that did upload still get linked by the caller.
 * A failed file keeps its row and its parts until `retryFailed` finishes it or
 * `cancelUpload` removes it.
 *
 * @module awsUploadService
 */

export interface MediaUploadMetadata {
  id: string | number;
  name: string;
  description: string;
  exif_metadata?: Record<string, any> | null;
  geometry?: { type: "Point"; coordinates: [number, number] } | null;
}

export interface MediaUploadSuccess {
  file: File;
  uploadType: "s3";
  mediaId: number;
  frontendId?: string;
}

export interface MediaUploadFailure {
  file: File;
  frontendId?: string;
  error: string;
}

export interface MediaUploadOutcome {
  succeeded: MediaUploadSuccess[];
  failed: MediaUploadFailure[];
}

const NOTHING: MediaUploadOutcome = { succeeded: [], failed: [] };

/**
 * Marks a landed file's row completed, which also counts it in its batch. The
 * server completes it from the key it issued, so the status is all it needs.
 */
const markUploaded = (file: EngineFile, batchId: number | undefined) =>
  apiFetch(`${process.env.REACT_APP_API_ROOT}api/v2/media/${file.meta.assetId}/`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ upload_details: { status: "completed", batch_id: batchId } })
  });

export const useS3Upload = () => {
  const [engine] = useState(() =>
    createS3Uppy(mediaAdapter, {
      maxFileSize: UPLOAD_OPTIONS?.MAX_FILE_SIZE || 4000 * 1024 * 1024, // 4Gb
      allowedFileTypes: allowedFileTypesToUpload as unknown as string[]
    })
  );
  const { uppy } = engine;
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<Record<string, FileProgress>>({});
  const [errors, setErrors] = useState<UploadError[]>([]);

  // The batch being uploaded; a cancel marks it failed.
  const batchRef = useRef<UploadBatch | null>(null);
  const cancelledRef = useRef(false);
  // Uppy ids of files whose row is marked completed: a cancel leaves these alone.
  const savedRef = useRef<Set<string>>(new Set());
  // Files that landed but whose row could not be marked completed; a retry tries again.
  const unsavedRef = useRef<Map<string, EngineFile>>(new Map());

  const setStage = useCallback((frontendId: string | undefined, patch: Partial<FileProgress>) => {
    if (!frontendId) return;
    setProgress((prev) => ({ ...prev, [frontendId]: { ...prev[frontendId], ...patch } }));
  }, []);

  useEffect(() => {
    const onAdded = (file: EngineFile) => {
      setStage(file.meta.frontendId, { stage: "presigning", completed: 0, total: file.size || 0, progress: 0 });
    };
    const onProgress = (
      file: EngineFile | undefined,
      { bytesUploaded, bytesTotal }: { bytesUploaded: number; bytesTotal: number | null }
    ) => {
      if (!file || !bytesTotal) return;
      setStage(file.meta.frontendId, {
        stage: "uploading",
        completed: bytesUploaded,
        total: bytesTotal,
        progress: Math.round((bytesUploaded / bytesTotal) * 100)
      });
    };
    const onError = (file: EngineFile | undefined, error: unknown) => {
      setStage(file?.meta.frontendId, { stage: "failed" });
      setErrors((prev) => [...prev, { file: file?.name || "Unknown file", stage: "upload", error: describeUploadError(error) }]);
    };

    uppy.on("file-added", onAdded);
    uppy.on("upload-progress", onProgress);
    uppy.on("upload-error", onError);
    return () => {
      uppy.off("file-added", onAdded);
      uppy.off("upload-progress", onProgress);
      uppy.off("upload-error", onError);
    };
  }, [uppy, setStage]);

  /**
   * Marks each landed file's row completed, reports every file's outcome, and
   * records the batch's result (a cancel records its own).
   */
  const finish = useCallback(
    async (landed: EngineFile[], failedFiles: EngineFile[], { cancelled = false } = {}): Promise<MediaUploadOutcome> => {
      const batch = batchRef.current;
      const saved = await Promise.all(
        landed.map(async (file) => {
          const frontendId = file.meta.frontendId as string | undefined;
          try {
            await markUploaded(file, batch?.id);
            // An open media list or detail shows the item as ready without a refetch.
            await applyAssetStatus(queryClient, file.meta.assetId as number, "completed", "media");
            savedRef.current.add(file.id);
            unsavedRef.current.delete(file.id);
            setStage(frontendId, { stage: "completed", progress: 100 });
            const success: MediaUploadSuccess = {
              file: file.data as File,
              uploadType: "s3",
              mediaId: file.meta.assetId as number,
              frontendId
            };
            return success;
          } catch (err) {
            console.error(`[upload] Could not mark media ${file.meta.assetId} uploaded`, err);
            unsavedRef.current.set(file.id, file);
            setStage(frontendId, { stage: "failed" });
            const failure: MediaUploadFailure = {
              file: file.data as File,
              frontendId,
              error: "Uploaded, but the media item could not be saved."
            };
            return failure;
          }
        })
      );

      const succeeded = saved.filter((r): r is MediaUploadSuccess => "mediaId" in r);
      const failed: MediaUploadFailure[] = [
        ...saved.filter((r): r is MediaUploadFailure => "error" in r),
        ...failedFiles.map((file) => ({
          file: file.data as File,
          frontendId: file.meta.frontendId as string | undefined,
          error: file.error || "Upload failed"
        }))
      ];

      if (batch?.id && !cancelled) {
        try {
          if (failed.length === 0) {
            await uploadBatchService.updateBatchStatus(batch.id, "completed");
          } else {
            await uploadBatchService.updateBatchStatus(batch.id, "failed", {
              type: "upload_failure",
              message: failed.map((f) => `${f.file.name}: ${f.error}`).join(", "),
              failed_files: failed.map((f) => ({
                file: f.file.name,
                error: f.error,
                error_type: "UPLOAD_ERROR",
                size: f.file.size,
                type: f.file.type
              })),
              error_summary: `${failed.length} of ${batch.file_count ?? failed.length + succeeded.length} file(s) failed`
            });
          }
        } catch (err) {
          console.error("[upload] Could not update the upload batch", err);
        }
      }

      return { succeeded, failed };
    },
    [setStage, queryClient]
  );

  const uploadFiles = useCallback(
    async (files: File[], metadata: MediaUploadMetadata[]): Promise<MediaUploadOutcome> => {
      // The previous batch's files are settled; a resumable one is in the resume store.
      uppy.cancelAll();
      cancelledRef.current = false;
      savedRef.current.clear();
      unsavedRef.current.clear();
      setProgress({});
      setErrors([]);

      const batch = await uploadBatchService.createBatch(files);
      batchRef.current = batch;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileMetadata = metadata[i];
        setProgress((prev) => ({ ...prev, [fileMetadata.id]: { stage: "pending" as const, completed: 0, total: file.size, progress: 0 } }));
        engine.addFile(
          file,
          {
            frontendId: fileMetadata.id,
            displayName: fileMetadata.name,
            name: fileMetadata.name,
            description: fileMetadata.description,
            batch_id: batch.id,
            exif_metadata: fileMetadata.exif_metadata ?? null,
            geometry: fileMetadata.geometry ?? null
          },
          await engine.findResumable(file)
        );
      }

      const result = await uppy.upload();
      // A cancel discarded the unfinished files; the ones that had landed are kept, and finished here.
      if (cancelledRef.current) return finish(result?.successful ?? [], [], { cancelled: true });
      if (!result) return NOTHING;
      return finish(result.successful ?? [], result.failed ?? []);
    },
    [engine, uppy, finish]
  );

  /** Retries the failed files; a multipart one continues from the parts S3 already holds. */
  const retryFailed = useCallback(async (): Promise<MediaUploadOutcome> => {
    cancelledRef.current = false;
    setErrors([]);
    const unsaved = Array.from(unsavedRef.current.values());
    const result = await uppy.retryAll();
    const landed = [...unsaved, ...(result?.successful ?? [])];
    if (cancelledRef.current) return finish(landed, [], { cancelled: true });
    return finish(landed, result?.failed ?? []);
  }, [uppy, finish]);

  /**
   * Gives up on the failed files: discards their parts and deletes their rows,
   * so nothing half-uploaded is left in the media list. Returns how many rows were removed.
   */
  const discardFailed = useCallback(async (): Promise<number> => {
    const failedIds = uppy
      .getFiles()
      .filter((file) => file.error || unsavedRef.current.has(file.id))
      .map((file) => file.id);
    unsavedRef.current.clear();
    return engine.discard(failedIds);
  }, [engine, uppy]);

  /**
   * Cancels the batch: stops every running and queued upload, discards their
   * parts and deletes their media rows (an unfinished row would otherwise show
   * up as a broken item), then marks the batch failed. Files whose bytes had
   * already landed are kept: the upload that is still resolving marks them
   * completed and returns them, so the caller links them as usual.
   *
   * @returns the number of media rows deleted, and of finished files kept.
   */
  const cancelUpload = useCallback(async (): Promise<{ deletedCount: number; keptCount: number }> => {
    cancelledRef.current = true;
    const batch = batchRef.current;
    const files = uppy.getFiles();
    const unfinished = files
      .filter((file) => !savedRef.current.has(file.id) && !(file.progress.uploadComplete && !file.error))
      .map((file) => file.id);
    const keptCount = files.length - unfinished.length;
    const deletedCount = await engine.discard(unfinished);

    if (batch?.id) {
      try {
        await uploadBatchService.updateBatchStatus(batch.id, "failed", {
          type: "cancelled",
          message: "Upload cancelled by user",
          error_summary: `Cancelled — ${deletedCount} partial item(s) deleted, ${keptCount} finished file(s) kept`
        });
      } catch (err) {
        console.error("Failed to update batch status after cancel:", err);
      }
    }

    setProgress({});
    setErrors([]);
    return { deletedCount, keptCount };
  }, [engine, uppy]);

  return {
    uploadFiles,
    retryFailed,
    discardFailed,
    cancelUpload,
    listInterrupted: engine.listInterrupted,
    discardInterrupted: engine.discardInterrupted,
    progress,
    errors
  };
};
